import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EmployeeService } from '../../../employee/services/employee.service';
import { Shift } from '../../../employee/models/employee.models';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { parseYmdToLocalNoon, toLocalCalendarYmd } from '../../utils/date.util';

export interface DaySchedule {
  day: string;
  dayDate: string; // ISO date string from API
  status: 'Working' | 'Leave' | 'Half day leave';
  startTime: string; // 12-hour format for display
  endTime: string; // 12-hour format for display
  originalStartTime?: string; // 24-hour format from API
  originalEndTime?: string; // 24-hour format from API
}

@Component({
  selector: 'app-edit-schedule-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './edit-schedule-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-schedule-modal.component.scss'],
})
export class EditScheduleModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() scheduleData: any = null; // The schedule to edit
  @Output() closeModal = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  employeeInfo: any = null;
  weekRange = '';
  selectedShiftId = '';
  selectedShiftName = '';
  shifts: Shift[] = [];
  scheduleId = '';
  weekStart = '';
  weekEnd = '';

  days: DaySchedule[] = [];

  // Store original state to detect changes
  originalShiftId = '';
  originalDays: DaySchedule[] = [];

  timeOptions: string[] = [];

  openShiftDropdown = false;
  openStatusDropdowns: Record<number, boolean> = {};
  openStartTimeDropdowns: Record<number, boolean> = {};
  openEndTimeDropdowns: Record<number, boolean> = {};

  isSubmitting = false;
  isLoadingShifts = false;
  isLoadingSchedule = false;

  constructor(
    private employeeService: EmployeeService,
    private messageService: NotificationService
  ) {
    this.generateTimeOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && changes['isOpen'].currentValue && this.scheduleData) {
      this.initializeForm();
      this.loadScheduleData();
      // Load shifts when modal opens to get latest data
      this.loadShifts();
    }
  }

  /**
   * Initialize component form state from scheduleData: sets employeeInfo (name, empId, designation, picture_url), scheduleId, weekStart/weekEnd/weekRange (using provided week or current week), and selected shift fields; calls ensureSelectedShift().
   * @example
   * this.initializeForm()
   * // undefined (method returns void; component fields are mutated)
   * @returns {void} No return value; updates component instance properties.
   **/
  private initializeForm(): void {
    // Initialize employee info
    // Use emp_id (format: "EMP#11250") if available, otherwise fall back to employee_id
    const displayEmpId = this.scheduleData.emp_id || this.scheduleData.employee_id || '';

    this.employeeInfo = {
      name: this.scheduleData.employee_name || '',
      empId: displayEmpId,
      designation: this.scheduleData.designation || 'Sales Associate',
      picture_url: this.scheduleData.picture_url || null,
    };

    // Extract schedule ID
    this.scheduleId = this.scheduleData.schedule_id || '';

    // Extract week dates if available
    if (this.scheduleData.week_start && this.scheduleData.week_end) {
      this.weekStart = this.scheduleData.week_start;
      this.weekEnd = this.scheduleData.week_end;
      this.weekRange = this.formatWeekRange(this.weekStart, this.weekEnd);
    } else {
      // Calculate week range from current week if not provided (Sunday to Saturday)
      const today = new Date();
      const sunday = this.getSunday(today);
      const saturday = new Date(sunday);
      saturday.setDate(saturday.getDate() + 6);
      this.weekStart = this.formatDateForAPI(sunday);
      this.weekEnd = this.formatDateForAPI(saturday);
      this.weekRange = this.formatWeekRange(this.weekStart, this.weekEnd);
    }

    // Set selected shift
    this.selectedShiftId = this.scheduleData.shift_id || '';
    this.selectedShiftName = this.scheduleData.shift_name || '';

    this.ensureSelectedShift();
  }

  /**
   * Loads schedule data for the current scheduleId and week range, updates component state, and handles success or error responses by parsing data or showing error messages.
   * @example
   * loadScheduleData()
   * undefined
   * @returns {{void}} Does not return a value; updates component state and may display messages on error.
   **/
  private loadScheduleData(): void {
    if (!this.scheduleId || !this.weekStart || !this.weekEnd) {
      console.error('Missing schedule ID or week dates');
      return;
    }

    this.isLoadingSchedule = true;
    this.employeeService
      .getEmployeeSchedule(this.scheduleId, this.weekStart, this.weekEnd)
      .subscribe({
        next: response => {
          this.isLoadingSchedule = false;
          if (response.status && response.data && Array.isArray(response.data)) {
            this.parseScheduleData(response.data);
          } else {
            console.error('Invalid schedule data format:', response);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load schedule data',
              life: 5000,
            });
          }
        },
        error: error => {
          this.isLoadingSchedule = false;
          console.error('Error loading schedule data:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load schedule data',
            life: 5000,
          });
        },
      });
  }

  /**
   * Parse an array of schedule items to populate this.days, determine shift selection, and store original state.
   * @example
   * parseScheduleData([{ day_date: '2026-03-01', start_time: '09:00', end_time: '17:00', attendance_status: 'working', shift_id: 1 }])
   * undefined
   * @param {any[]} scheduleData - Array of schedule objects (e.g., API response) to parse for a week's schedule.
   * @returns {void} Does not return a value; updates component state (this.days, selectedShiftId/Name) and stores original state.
   **/
  private parseScheduleData(scheduleData: any[]): void {
    // Initialize days array with all 7 days of the week (Sunday to Saturday)
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    this.days = [];

    // Create a map of date to schedule data
    const scheduleMap = new Map<string, any>();
    scheduleData.forEach(item => {
      if (item.day_date) {
        const cal = toLocalCalendarYmd(item.day_date);
        const date = parseYmdToLocalNoon(cal);
        if (isNaN(date.getTime())) {
          return;
        }
        const dayIndex = date.getDay();
        scheduleMap.set(dayIndex.toString(), item);
      }
    });

    // Populate days array
    for (let i = 0; i < 7; i += 1) {
      const scheduleItem = scheduleMap.get(i.toString());
      const dayName = dayNames[i];

      if (scheduleItem) {
        const startTime = scheduleItem.start_time
          ? this.convert24To12Hour(scheduleItem.start_time)
          : '8:00 AM';
        const endTime = scheduleItem.end_time
          ? this.convert24To12Hour(scheduleItem.end_time)
          : '7:00 PM';

        // Ensure selected shift details are populated from the schedule data
        // Try to get shift_id from schedule item, or look it up by shift_name
        if (!this.selectedShiftId) {
          if (scheduleItem.shift_id) {
            this.selectedShiftId = scheduleItem.shift_id;
          } else if (scheduleItem.shift_name && this.shifts.length > 0) {
            // Look up shift_id by shift_name if shift_id is not in response
            const shift = this.shifts.find(s => s.shift_name === scheduleItem.shift_name);
            if (shift) {
              this.selectedShiftId = shift.id;
            }
          }
        }
        if (!this.selectedShiftName && scheduleItem.shift_name) {
          this.selectedShiftName = scheduleItem.shift_name;
        }

        // Determine status based on API response
        // Check attendance_status and is_working to determine the correct status
        let dayStatus: 'Working' | 'Leave' | 'Half day leave' = 'Working';

        if (scheduleItem.attendance_status) {
          const attendanceStatus = scheduleItem.attendance_status.toLowerCase().trim();

          // Handle half day cases (check for "half" in the status)
          if (
            attendanceStatus.includes('half') ||
            attendanceStatus === 'halfday' ||
            attendanceStatus === 'half day'
          ) {
            dayStatus = 'Half day leave';
          }
          // Handle leave cases
          else if (attendanceStatus === 'leave' || attendanceStatus === 'off') {
            dayStatus = 'Leave';
          }
          // Handle working cases
          else if (attendanceStatus === 'working' || attendanceStatus === 'present') {
            dayStatus = 'Working';
          }
          // Handle absent - if is_working is true, they're scheduled to work (Working status)
          // If is_working is false, it's Leave
          else if (attendanceStatus === 'absent') {
            dayStatus = scheduleItem.is_working === true ? 'Working' : 'Leave';
          }
          // Default: use is_working to determine status
          else {
            dayStatus = scheduleItem.is_working === true ? 'Working' : 'Leave';
          }
        } else {
          const isWorking = scheduleItem.is_working === true;
          if (!isWorking) {
            dayStatus = 'Leave';
          } else if (scheduleItem.is_half_day === true) {
            dayStatus = 'Half day leave';
          } else {
            dayStatus = 'Working';
          }
        }

        // Determine if times should be shown (only for Working or Half day leave)
        const shouldShowTimes = dayStatus === 'Working' || dayStatus === 'Half day leave';

        this.days.push({
          day: dayName,
          dayDate: scheduleItem.day_date,
          status: dayStatus,
          startTime: shouldShowTimes ? startTime : '8:00 AM',
          endTime: shouldShowTimes ? endTime : '7:00 PM',
          originalStartTime: scheduleItem.start_time || null,
          originalEndTime: scheduleItem.end_time || null,
        });
      } else {
        // Default day if no data
        this.days.push({
          day: dayName,
          dayDate: '',
          status: 'Leave',
          startTime: '8:00 AM',
          endTime: '7:00 PM',
        });
      }
    }

    // Ensure we have a valid shift selection before storing original state
    this.ensureSelectedShift();

    // Store original state after parsing schedule data
    this.storeOriginalState();
  }

  /**
   * Store the current selected shift ID and a deep copy of the days array as the original state.
   * @example
   * this.storeOriginalState()
   * // undefined
   * @returns {void} Does not return a value.
   **/
  private storeOriginalState(): void {
    // Store original shift ID
    this.originalShiftId = this.selectedShiftId || '';

    // Deep copy of original days array
    this.originalDays = this.days.map(day => ({
      day: day.day,
      dayDate: day.dayDate,
      status: day.status,
      startTime: day.startTime,
      endTime: day.endTime,
      originalStartTime: day.originalStartTime,
      originalEndTime: day.originalEndTime,
    }));
  }

  /**
   * Determine whether the current schedule selections differ from the original saved state.
   * @example
   * asChanges()
   * true
   * @returns {boolean} True if any shift selection, day status, start time, or end time has changed; otherwise false.
   **/
  hasChanges(): boolean {
    // If no original state stored yet, return false (data still loading)
    if (!this.originalDays.length && this.days.length === 0) {
      return false;
    }

    // Check if shift has changed
    if (this.selectedShiftId !== this.originalShiftId) {
      return true;
    }

    // Check if any day has changed (status, startTime, or endTime)
    if (this.days.length !== this.originalDays.length) {
      return true;
    }

    for (let i = 0; i < this.days.length; i += 1) {
      const currentDay = this.days[i];
      const originalDay = this.originalDays[i];

      if (!originalDay) {
        return true;
      }

      // Check if status changed
      if (currentDay.status !== originalDay.status) {
        return true;
      }

      // Check if times changed (for working/half day leave days)
      if (currentDay.status === 'Working' || currentDay.status === 'Half day leave') {
        if (currentDay.startTime !== originalDay.startTime) {
          return true;
        }
        if (currentDay.endTime !== originalDay.endTime) {
          return true;
        }
      }
    }

    return false;
  }

  private convert24To12Hour(time24: string): string {
    if (!time24) return '8:00 AM';

    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    let hours12 = hours % 12;
    if (hours12 === 0) hours12 = 12;

    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Convert a 12-hour time string with AM/PM to a 24-hour "HH:MM:SS" formatted string.
   * @example
   * convert12To24Hour('07:15 PM')
   * '19:15:00'
   * @param {{string}} {{time12}} - Time in 12-hour format (e.g. "07:15 PM", "12:00 AM").
   * @returns {{string}} 24-hour time string formatted as "HH:MM:SS" or default "08:00:00" if input is falsy.
   **/
  private convert12To24Hour(time12: string): string {
    if (!time12) return '08:00:00';

    const [time, period] = time12.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let hours24 = hours;

    if (period === 'PM' && hours !== 12) {
      hours24 = hours + 12;
    } else if (period === 'AM' && hours === 12) {
      hours24 = 0;
    }

    return `${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  }

  private formatWeekRange(weekStart: string, weekEnd: string): string {
    const startDate = parseYmdToLocalNoon(weekStart.split('T')[0]);
    const endDate = parseYmdToLocalNoon(weekEnd.split('T')[0]);

    const startDay = this.getDayName(startDate);
    const endDay = this.getDayName(endDate);
    const startDateStr = this.formatDate(startDate);
    const endDateStr = this.formatDate(endDate);

    return `${startDay}, ${startDateStr} - ${endDay}, ${endDateStr}`;
  }

  private getDayName(date: Date): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  }

  private formatDate(date: Date): string {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  private formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getSunday(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day; // Sunday = 0, so subtract day to get to Sunday
    const sunday = new Date(date);
    sunday.setDate(diff);
    return sunday;
  }

  /**
   * Load shifts from EmployeeService and update component state (sets this.shifts, toggles this.isLoadingShifts, and calls ensureSelectedShift on success).
   * @example
   * loadShifts()
   * undefined
   * @param {void} none - This function does not accept any arguments.
   * @returns {void} Performs asynchronous loading of shifts, updates component state, and handles errors; does not return a value.
   **/
  private loadShifts(): void {
    this.isLoadingShifts = true;
    this.employeeService.loadShifts().subscribe({
      next: shifts => {
        this.shifts = shifts || [];
        this.isLoadingShifts = false;
        // After shifts are loaded, ensure the selected shift is properly set
        this.ensureSelectedShift();
      },
      error: error => {
        console.error('Error loading shifts:', error);
        this.shifts = [];
        this.isLoadingShifts = false;
      },
    });
  }

  /**
   * Generate and assign an array of 12-hour clock time strings at 30-minute intervals to this.timeOptions.
   * @example
   * this.generateTimeOptions()
   * // this.timeOptions -> ['12:00 AM', '12:30 AM', '1:00 PM', '1:30 PM', ..., '12:00 AM', '12:30 AM']
   * @returns {void} Populates this.timeOptions with 12-hour formatted times (every 30 minutes) and appends midnight entries.
   */
  private generateTimeOptions(): void {
    const times: string[] = [];
    const periods = ['AM', 'PM'];

    for (const period of periods) {
      for (let hour = period === 'AM' ? 12 : 1; hour <= 12; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const hourStr = hour.toString();
          const minuteStr = minute === 0 ? '00' : minute.toString();
          times.push(`${hourStr}:${minuteStr} ${period}`);
        }
      }
      if (period === 'PM') {
        times.push('12:00 AM');
        times.push('12:30 AM');
      }
    }

    this.timeOptions = times;
  }

  /**
   * Ensure the component's selected shift id and name are synchronized and set to a default when missing.
   * @example
   * ensureSelectedShift()
   * // returns void
   * @param {void} none - This function does not accept any parameters.
   * @returns {void} No return value; updates component state (selectedShiftId and selectedShiftName) as needed.
   **/
  private ensureSelectedShift(): void {
    if (this.selectedShiftId) {
      if (!this.selectedShiftName && this.shifts.length > 0) {
        const shiftById = this.shifts.find(s => s.id === this.selectedShiftId);
        if (shiftById) {
          this.selectedShiftName = shiftById.shift_name;
        }
      }
      return;
    }

    if (this.selectedShiftName && this.shifts.length > 0) {
      const shiftByName = this.shifts.find(s => s.shift_name === this.selectedShiftName);
      if (shiftByName) {
        this.selectedShiftId = shiftByName.id;
        return;
      }
    }

    if (!this.selectedShiftId && !this.selectedShiftName && this.shifts.length > 0) {
      const [firstShift] = this.shifts;
      this.selectedShiftId = firstShift.id;
      this.selectedShiftName = firstShift.shift_name;
    }
  }

  toggleShiftDropdown(): void {
    if (!this.isSubmitting) {
      this.openShiftDropdown = !this.openShiftDropdown;
    }
  }

  selectShift(shiftId: string): void {
    this.selectedShiftId = shiftId;
    const shift = this.shifts.find(s => s.id === shiftId);
    this.selectedShiftName = shift ? shift.shift_name : '';
    this.openShiftDropdown = false;
  }

  getSelectedShiftName(): string {
    if (this.selectedShiftName) {
      return this.selectedShiftName;
    }
    const shift = this.shifts.find(s => s.id === this.selectedShiftId);
    return shift ? shift.shift_name : 'Select shift';
  }

  toggleStatusDropdown(index: number): void {
    if (!this.isSubmitting) {
      this.openStatusDropdowns[index] = !this.openStatusDropdowns[index];
    }
  }

  toggleStartTimeDropdown(index: number): void {
    if (!this.isSubmitting) {
      this.openStartTimeDropdowns[index] = !this.openStartTimeDropdowns[index];
    }
  }

  toggleEndTimeDropdown(index: number): void {
    if (!this.isSubmitting) {
      this.openEndTimeDropdowns[index] = !this.openEndTimeDropdowns[index];
    }
  }

  /**
   * Change the status of a specific day, update default times when appropriate, and close its status dropdown.
   * @example
   * nDayStatusChange(1, 'Leave')
   * // void; days[1].status === 'Leave', days[1].startTime === '8:00 AM', days[1].endTime === '7:00 PM', openStatusDropdowns[1] === false
   * @param {{number}} {{index}} - Index of the day in the days array to modify.
   * @param {{'Working' | 'Leave' | 'Half day leave'}} {{status}} - New status to set for the given day.
   * @returns {{void}} Return nothing.
   **/
  onDayStatusChange(index: number, status: 'Working' | 'Leave' | 'Half day leave'): void {
    this.days[index].status = status;
    if (
      status === 'Leave' ||
      !this.days[index].startTime ||
      this.days[index].startTime === '8:00 AM'
    ) {
      this.days[index].startTime = '8:00 AM';
      this.days[index].endTime = '7:00 PM';
    }
    this.openStatusDropdowns[index] = false;
  }

  onDayStartTimeChange(index: number, time: string): void {
    this.days[index].startTime = time;
    this.openStartTimeDropdowns[index] = false;
  }

  onDayEndTimeChange(index: number, time: string): void {
    this.days[index].endTime = time;
    this.openEndTimeDropdowns[index] = false;
  }

  getEmployeeInitials(): string {
    if (!this.employeeInfo?.name) return '';
    const names = this.employeeInfo.name.split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return this.employeeInfo.name.charAt(0).toUpperCase();
  }

  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  onClose(): void {
    if (!this.isSubmitting) {
      this.closeModal.emit();
    }
  }

  onCancel(): void {
    this.onClose();
  }

  /**
   * Prepare attendance payload from the current days, call the API to update the employee schedule, handle success/error messages and emit events, while preventing duplicate submissions.
   * @example
   * nSave()
   * void
   * @returns {{void}} Does not return a value; performs an API update and handles responses via side effects.
   **/
  onSave(): void {
    if (this.isSubmitting || !this.selectedShiftId) {
      return;
    }

    this.isSubmitting = true;

    // Prepare the data to send to API
    // The API expects attendance array with date and status
    const attendance = this.days.map(day => {
      const formattedDate = day.dayDate
        ? toLocalCalendarYmd(day.dayDate)
        : this.getDateForDay(day.day);

      // Map status values for API: convert to lowercase - "working", "leave", "half day"
      let apiStatus: string;
      if (day.status === 'Half day leave') {
        apiStatus = 'half day';
      } else if (day.status === 'Working') {
        apiStatus = 'working';
      } else {
        apiStatus = 'leave'; // "Leave" -> "leave"
      }

      return {
        date: formattedDate,
        status: apiStatus,
      };
    });

    const updateData = {
      employee_id: this.scheduleData.employee_id,
      shift_id: this.selectedShiftId,
      attendance: attendance,
    };

    this.employeeService.updateEmployeeSchedule(this.scheduleId, updateData).subscribe({
      next: response => {
        this.isSubmitting = false;
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Schedule updated successfully',
            life: 3000,
          });
          this.saveSuccess.emit();
          this.closeModal.emit();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to update schedule',
            life: 5000,
          });
        }
      },
      error: error => {
        this.isSubmitting = false;
        console.error('Error updating schedule:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to update schedule',
          life: 5000,
        });
      },
    });
  }

  /**
   * Calculate the formatted date string for the specified weekday within the current week (based on this.weekStart).
   * @example
   * getDateForDay('Tuesday')
   * '2021-09-14'
   * @param {{string}} {{dayName}} - Day name (e.g. 'Monday', 'Tuesday').
   * @returns {{string}} Formatted date string for that day in the current week, or '' if dayName is invalid.
   **/
  private getDateForDay(dayName: string): string {
    // Calculate the date for the given day name within the current week (Sunday = index 0 … Saturday = 6).
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayIndex = dayNames.indexOf(dayName);
    if (dayIndex === -1) return '';

    const base = parseYmdToLocalNoon(this.weekStart.split('T')[0]);
    if (isNaN(base.getTime())) return '';

    const targetDate = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate() + dayIndex,
      12,
      0,
      0,
      0
    );

    return this.formatDateForAPI(targetDate);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
